import React, { useEffect, useState, useRef } from 'react';
import { replaceClassName, addClassName, removeClassName, findDescendentById } from '../../Utils/HTMLDom'
import { SkitterPage } from '../../Models/SkitterPage';
import { ReactComponent as BookPagesSimulation } from '../../../public/images/svg/NewBookSimulator.svg';
import './css/BookPagesSimulator.css';

interface BookPagesSimulatorProps {
    images: SkitterPage[]; // list of images that will form a skitterbook in base64. The each image should contain a bend deformation.
    flipSpeedppms: number; // speed of required skitterbook flip in pixels per millisecond
    resetTimestamp: string;
    useFlipVibration: boolean;
    imageMarginMm: number;
    onComplete?: (event: { pages: number }) => void;
}

const maskAreaElementId = "innerRightPage";
const rightPageAreaElementId = "rightPage";

const BookPagesSimulator: React.FC<BookPagesSimulatorProps> = ({ images, flipSpeedppms, resetTimestamp, useFlipVibration = true, imageMarginMm, onComplete }) => {
    const [progress, setProgress] = useState(0);
    const [progressFactor, setProgressFactor] = useState(0);
    const [flipViewingComplete, setFlipViewingComplete] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const bookSimulatorBodyRef = useRef<HTMLDivElement>(null);
    const NUM_STACK_PAGES = 40;
    const flipTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const flipIndexRef = useRef(0);

    useEffect(() => {
        if (containerRef.current) {
            setProgressFactor(100 / images.length);
        }
    }, [progress]);

    useEffect(() => {
        const container = containerRef.current;
        const maskAreaElement = findDescendentById(container, maskAreaElementId);
        const rightPageAreaElement = findDescendentById(container, rightPageAreaElementId);

        const updateRect = () => {
            if (maskAreaElement && rightPageAreaElement && container) {
                var maskRect = maskAreaElement.getBoundingClientRect();
                var rightPageRect = rightPageAreaElement.getBoundingClientRect();
                var containerRefRect = container.getBoundingClientRect();
                if (maskRect && rightPageRect && containerRefRect) {
                    var imageMaskOffsetX = maskRect.x - containerRefRect.x;
                    var imageMaskOffsetY = maskRect.y - containerRefRect.y;

                    var rightPageOffsetX = rightPageRect.x - containerRefRect.x;

                    container.style.setProperty("--image-mask-x", `${imageMaskOffsetX}px`);
                    container.style.setProperty("--image-mask-y", `${imageMaskOffsetY}px`);
                    container.style.setProperty("--image-mask-width", `${maskRect.width}px`);
                    container.style.setProperty("--image-mask-height", `${maskRect.height}px`);

                    container.style.setProperty("--right-page-x", `${rightPageOffsetX}px`);
                    container.style.setProperty("--right-page-width", `${rightPageRect.width}px`);
                }
            }
        };

        updateRect();

        // Keep it in sync with layout changes 
        const ro = new ResizeObserver(updateRect);

        if (container) {
            ro.observe(container);
        }

        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (resetTimestamp != "") {
            for (let index = 0; index <= NUM_STACK_PAGES; index++) {
                const leftProgressStackPage = findDescendentById(container, `left-stack-pg-${index}`);
                const rightProgressStackPage = findDescendentById(container, `right-stack-pg-${index}`);
                const leftPageLayer = findDescendentById(container, 'leftPageLayer');
                const rightPageLayer = findDescendentById(container, 'rightPageLayer');
                const rightPageUnderlay = findDescendentById(container, 'rightPageUnderlay');
                const holdingThumb = findDescendentById(container, 'holdingThumb');
                if (leftProgressStackPage && rightProgressStackPage && leftPageLayer && rightPageLayer && rightPageUnderlay && holdingThumb) {
                    replaceClassName(leftProgressStackPage, "show", "hide");
                    replaceClassName(rightProgressStackPage, "hide", "show");
                    replaceClassName(leftPageLayer, "show", "hide");
                    replaceClassName(rightPageLayer, "hide", "show");
                    replaceClassName(rightPageUnderlay, "hide", "show");
                    replaceClassName(holdingThumb, "hide", "show");
                }
                setFlipViewingComplete(false);
                setProgress(0);
                flipIndexRef.current = 0;
            }
        }
    }, [resetTimestamp]);

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            const rightPage = findDescendentById(container, `rightPage`);
            if (rightPage) {
                rightPage.style.strokeWidth = `${imageMarginMm}mm`;
            }
        }
    }, [imageMarginMm])

    useEffect(() => {
        const container = containerRef.current;
        if (flipSpeedppms > 0) {
            if (flipTimerRef.current) {
                clearInterval(flipTimerRef.current);
            }

            if (bookSimulatorBodyRef.current) {
                addClassName(bookSimulatorBodyRef.current as Element, "turning");
            }

            const flipSpeedMs = Math.max(Math.min((1 / flipSpeedppms), 1000), 40); // ensure that the result flip speed bounds is
            // between 1000ms (slow) and 40ms (fast);

            flipTimerRef.current = setInterval(() => {
                const frameElement = findDescendentById(container, "frame-" + flipIndexRef.current);
                if (frameElement && progress <= 100) {
                    replaceClassName(frameElement, "unflipped", "flipped");

                    const flipProgress = ((flipIndexRef.current + 1) * progressFactor);
                    setProgress(flipProgress);

                    const progressStackPageNum = Math.round(flipProgress * (NUM_STACK_PAGES / 100));

                    const leftProgressStackPage = findDescendentById(container, `left-stack-pg-${progressStackPageNum}`);
                    const rightProgressStackPage = findDescendentById(container, `right-stack-pg-${progressStackPageNum}`);
                    const leftPageLayer = findDescendentById(container, 'leftPageLayer');
                    const holdingThumb = findDescendentById(container, 'holdingThumb');

                    if (useFlipVibration === true && "vibrate" in navigator) {
                        navigator.vibrate(20)
                    }

                    if (flipProgress >= 100) {
                        const rightPageLayer = findDescendentById(container, "rightPageLayer");
                        const rightPageUnderlay = findDescendentById(container, "rightPageUnderlay");
                        if (rightPageLayer && rightPageUnderlay) {
                            replaceClassName(rightPageLayer, "show", "hide");
                            replaceClassName(rightPageUnderlay, "show", "hide");
                            setFlipViewingComplete(true);
                            if (bookSimulatorBodyRef.current) {
                                removeClassName(bookSimulatorBodyRef.current as Element, "turning");
                            }
                            onComplete?.({ pages: images.length });
                        }
                    }

                    if (progressFactor > 0) {
                        if (leftProgressStackPage && rightProgressStackPage) {
                            replaceClassName(leftProgressStackPage, "hide", "show");
                            replaceClassName(rightProgressStackPage, "show", "hide");
                        }
                        if (leftPageLayer && holdingThumb) {
                            replaceClassName(leftPageLayer, "hide", "show");
                            replaceClassName(holdingThumb, "show", "hide");
                        }
                    }
                }

                flipIndexRef.current += 1;
            }, flipSpeedMs);

        }
        else {
            if (flipTimerRef.current) {
                clearInterval(flipTimerRef.current);
            }
            if (bookSimulatorBodyRef.current) {
                removeClassName(bookSimulatorBodyRef.current as Element, "turning");
            }
        }
    }, [flipSpeedppms])

    const flipPageListContent = images && images.length > 0 && !flipViewingComplete
        ? <div className="flipPageList">
            {
                images.map((image, index) => <div key={image.frameId} id={`frame-${index}`} className={`flipPage percent100 ${image.isCover ? "flipped" : "unflipped"}`}>
                    <img src={`data:image/jpeg;base64,${image.imageBytes}`} />
                </div>)
            }
        </div>
        : <></>

    return (
        <div className="book-simulator" ref={containerRef}>
            <div className="book-simulator-body" ref={bookSimulatorBodyRef}>
                <div id="rightPageUnderlay" className="right-page-underlay show" />
                {flipPageListContent}
                <BookPagesSimulation preserveAspectRatio="xMidYMid meet" width="415.748" height="182.386" viewBox="0 0 415.748 182.386" />
            </div>
        </div>
    );
};

export default BookPagesSimulator;
